import { RefObject, useEffect, useId } from 'react'

import {
  isTopEscapeOwner,
  pushEscapeOwner,
  removeEscapeOwner,
} from './escapeStack'

/**
 * Calls `onEscape` when Escape is pressed, and claims the key so no ancestor
 * overlay also acts on it. Set `enabled` to `false` to suspend the listener
 * (e.g. when the dropdown it dismisses is closed).
 *
 * The listener is registered in the **capture** phase, and that is the whole
 * point of this hook rather than an inline `keydown` effect. A dropdown open
 * inside a Chakra dialog is the innermost open thing, so Escape belongs to it —
 * but Chakra's dialog `onKeyDown` calls `event.stopPropagation()` before
 * closing, and React 18 dispatches synthetic events at its root container. A
 * bubble-phase `document` listener therefore never runs at all: the drawer
 * closes and the dropdown never learns the key was pressed. Capturing at
 * `document` runs ahead of React's dispatch, so stopping propagation here keeps
 * the key from ever reaching the dialog. (ENG-32100)
 *
 * Ownership is arbitrated by the shared stack in `escapeStack.ts`, so a
 * dropdown open inside a Modal dismisses alone on the first press.
 *
 * Deliberately does not move focus. The incident drawer mounts with
 * `autoFocus={false}` so ArrowUp/ArrowDown keeps navigating the incidents table
 * behind it; pulling focus to the dropdown trigger on dismiss would break that.
 *
 * `ownerRef` opts into the same foreign-dialog guard `useOverlayStack` applies:
 * a Chakra v1 dialog can open above this owner without ever joining the escape
 * stack, and it traps focus. Pass the ref when the owner is something that
 * stays open behind such a dialog — a sub-drawer — so Escape isn't swallowed
 * from the visible dialog to close a hidden drawer. Dropdowns omit it: they
 * already dismiss on the outside mousedown that opens the dialog, and scoping
 * them would hand Escape to the dialog whenever focus sat in its body while
 * the dropdown was still the innermost open thing.
 */
export function useEscapeKey(
  onEscape: () => void,
  enabled = true,
  ownerRef?: RefObject<HTMLElement | null>
) {
  const id = useId()

  // Stack membership is kept in its own effect so it tracks `enabled` alone.
  // Folding it into the listener effect below would pop and re-push on every
  // change of `onEscape` identity, silently reordering the stack.
  useEffect(() => {
    if (!enabled) return
    pushEscapeOwner(id)
    return () => removeEscapeOwner(id)
  }, [enabled, id])

  useEffect(() => {
    if (!enabled) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Something newer is open on top of this one; it owns the key.
      if (!isTopEscapeOwner(id)) return
      // Same rule as useOverlayStack: a foreign dialog holding focus owns the
      // keyboard, so don't dismiss underneath it. Scoped to dialog ancestors —
      // focus merely sitting elsewhere (a toast action, a programmatic
      // .focus()) must not disable Escape. Only applies when the caller passed
      // an ownerRef; without one there is no subtree to compare against.
      if (ownerRef) {
        const active = document.activeElement
        if (
          active &&
          !ownerRef.current?.contains(active) &&
          active.closest('[role="dialog"], [aria-modal="true"]')
        ) {
          return
        }
      }
      e.stopPropagation()
      onEscape()
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [enabled, id, onEscape, ownerRef])
}
